import * as React from 'react';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Permission, Query as ApiQuery, CoughSessionStatusEnum, OverreadStatusIdEnum, QueryFilter, DeviceTypes, CoughProcessingAlgorithmEnum } from '../../misc/api/apiTypes';
import { PrimaryAnalysisSetModal } from './Modals/PrimaryAnalysisSetModal';
import { TrackingNumberModal } from './Modals/TrackingNumberModal';
import { PostalReceivedModal } from './Modals/PostalReceivedModal';
import { ReprocessCoughSessionModal } from './Modals/ReprocessCoughSessionModal';
import { Authorization } from "../../misc/components/Authorization";
import { HamburgerMenu } from '../../misc/components/HamburgerMenu';
import { formatNull, formatDateTime } from "../../misc/components/Formatters/Formatters";
import Condition from "../../misc/components/Condition";
import { SetAsReceivedMenuItem } from './MenuItems/SetAsReceived';
import { UnassignDeviceAuth } from './MenuItems/UnassignDeviceAuth';
import { UnassignDeviceModal } from './Modals/UnassignDeviceModal';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { Column, Table } from '../../Components/QueryTable';
import { useQuery } from '../../Components/Query';
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { WebPortalClient, CoughSession } from 'webportalclient';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { SortHeader } from '../../Components/SortHeader';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { useSecurityContext } from '../../Core/SecurityContext';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { uniqBy } from '../../misc/ArrayFunctions';
import { BatchForSessionsModal } from './Modals/BatchForSessionsModal';
import { BatchReprocessModal } from './Modals/BatchReprocessModal';

export interface CoughSessionsListTableRouterProps {
    statusId: string;
}

export interface CoughSessionsListTableState {
    showSiteModal: boolean;
    showDeviceSerialModal: boolean;
    showSubjectModal: boolean;
    showRandomizationStatusModal: boolean;
    showRecordingStartModal: boolean;
    showStatusModal: boolean;
}

declare const rootUrl: string;

export const CoughSessionsListTable = ({ }) => {
    const { data, applyFilters, filters } = useQuery();
    const { study, coughSettings, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { subjectTypes, coughSessionStatuses, deviceTypes } = useReferenceContextProvider();


    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [serialNumberOptions, setSerialNumberOptions] = React.useState([]);

    const [selectedCoughSession, setSelectedCoughSession] = React.useState<CoughSession>();
    const [showTrackingModal, setShowTrackingModal] = React.useState(false);
    const [showPostalModal, setShowPostalModal] = React.useState(false);
    const [showUnassignModal, setShowUnassignModal] = React.useState(false);
    const [showPrimaryAnalysisSetModal, setShowPrimaryAnalysisSetModal] = React.useState(false);
    const [showReprocessModal, setShowReprocessModal] = React.useState(false);
    const [showBatchForSessionsModal, setShowBatchForSessionsModal] = React.useState(false);
    const [showBatchReprocessModal, setShowBatchReprocessModal] = React.useState<'hidden' | 'all' | 'abacas' | 'manchester' | 'vta-ct1'>('hidden');

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `study/id eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/study/id eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getStudyScheduleVisits("id,label,studySchedule", `studySchedule/studyId eq ${study.id}`, "label", "studySchedule($select=studyId)");
            const options = visits.value.map(s => ({
                value: s.label,
                label: s.label,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getStudyScheduleSessions("id,label", `studyScheduleVisit/studySchedule/studyId eq ${study.id} and sessionTypeId eq 'ST_COUGH'`, "label", "studyScheduleVisit($expand=studySchedule)");
            const options = sessions.value.map(s => ({
                value: s.label,
                label: s.label,
            }));

            setSessionOptions(options);
        };

        const getSerialNumbers = async () => {
            const serialsResult = await client.client.getCoughSessions("serialNumber", `visit/subject/site/studyId eq ${study.id}`);
            const serialOptions = uniqBy(serialsResult.value, (a, b) => a.serialNumber === b.serialNumber).map(d => ({
                value: d.serialNumber,
                label: d.serialNumber !== undefined && d.serialNumber !== null ? d.serialNumber : "-"
            }));

            setSerialNumberOptions(serialOptions);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getSerialNumbers();
    }, []);

    const RenderHamburgerMenu = (row: CoughSession) => {
        return (
            <HamburgerMenu contextMenuLeftAligned={true}>
                <UnassignDeviceAuth
                    record={row}
                >
                    <li>
                        <a onClick={() => { setSelectedCoughSession(row); setShowUnassignModal(true); }}>Unassign Device</a>
                    </li>
                </UnassignDeviceAuth>
                <Authorization permissionId={PermissionEnum.CanPostMedia}
                    studyId={study.id}
                    currentPermissions={currentUserPermissions}
                >
                    <Condition test={() => row.postalTrackingNumber === null}>
                        <li>
                            <a onClick={() => { setSelectedCoughSession(row); setShowTrackingModal(true); }}>Add Tracking Number</a>
                        </li>
                    </Condition>
                    <Condition test={() => row.postalTrackingNumber !== null}>
                        <li>
                            <a onClick={() => { setSelectedCoughSession(row); setShowTrackingModal(true); }}>View/Edit Tracking Number</a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.SetPostedCoughSessionToReceived} currentPermissions={currentUserPermissions}
                    studyId={study.id}>
                    <Condition test={() => [CoughSessionStatusEnum.AwaitingData, CoughSessionStatusEnum.ReceivingData, CoughSessionStatusEnum.ReceivingDataMailed].includes(row.status as CoughSessionStatusEnum)}>
                        <SetAsReceivedMenuItem id={row.id} onComplete={() => applyFilters([])} />
                    </Condition>
                </Authorization>
                <Condition test={() => row.postalReceived !== null}>
                    <li>
                        <a onClick={() => { setShowPostalModal(true); setSelectedCoughSession(row); }}>Show Postal Received Date</a>
                    </li>
                </Condition>
                <Authorization permissionId={PermissionEnum.SetPrimaryAnalysisSets}
                    studyId={study.id}
                    currentPermissions={currentUserPermissions}
                >
                    <Condition test={() => row.analysisSets.filter(x => x.status === OverreadStatusIdEnum.Accepted || x.status === OverreadStatusIdEnum.Rejected).length >= 1}>
                        <li>
                            <a onClick={() => { setShowPrimaryAnalysisSetModal(true); setSelectedCoughSession(row); }}>Set Primary Analysis Sets</a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.ReProcessCoughSession} studyId={study.id} currentPermissions={currentUserPermissions}>
                    <Condition test={() => row.status === CoughSessionStatusEnum.AwaitingProcessing || row.status === CoughSessionStatusEnum.Processing || row.status === CoughSessionStatusEnum.AwaitingQualityCheck || row.status === CoughSessionStatusEnum.AwaitingAnalysis || row.status === CoughSessionStatusEnum.AnalysisInProgress}>
                        <li>
                            <a onClick={() => { setShowReprocessModal(true); setSelectedCoughSession(row); }}>Reprocess Cough Session</a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    }

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const triggerBatch = () => {
        setShowBatchForSessionsModal(true);
    }

    const triggerBatchReprocess = (algorithm: 'all' | 'abacas' | 'manchester' | 'vta-ct1') => {
        setShowBatchReprocessModal(algorithm);
    }

    const booleanOptions = [{ label: "Yes", value: true }, { label: "No", value: false }];

    if (subjectTypes.length === 0 || coughSessionStatuses.length === 0 || coughSettings == null) {
        return (<LoadingWithMessage message="Loading Cough Sessions..." />);
    }

    const subjectTypesOptions = subjectTypes.map(st => ({
        value: st.id,
        label: st.name,
    }));

    const coughSessionStatusesOptions = coughSessionStatuses.map(css => ({
        value: css.id,
        label: css.name
    }));

    const displayRandomisationColumn = () => coughSettings != null && coughSettings.DisplayRandomisationColumn;

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Cough Sessions</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization
                            permissionId={PermissionEnum.BatchProcessCoughSessions}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <button className="btn btn-primary btn-outline" onClick={triggerBatch}>
                                Batch Re-calculate
                            </button>&nbsp;
                            <div className="btn-group" role="group">
                                <button type="button" id="batchReprocess" className="btn btn-primary btn-outline dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    Batch Re-process
                                    <span className="caret"></span>
                                </button>
                                <ul className="dropdown-menu scrollable-menu pull-right" role="menu" aria-labelledby="batchReprocess">
                                    <li><a className="dropdown-item" onClick={() => triggerBatchReprocess('all')}>All</a></li>
                                    {coughSettings.ProcessingAlgorithms.includes(1) ? <li><a className="dropdown-item" onClick={() => triggerBatchReprocess('manchester')}>Manchester</a></li> : null}
                                    {coughSettings.ProcessingAlgorithms.includes(2) ? <li><a className="dropdown-item" onClick={() => triggerBatchReprocess('abacas')}>Abacas</a></li> : null}
                                    {coughSettings.ProcessingAlgorithms.includes(4) ? <li><a className="dropdown-item" onClick={() => triggerBatchReprocess('vta-ct1')}>VTA-CT1</a></li> : null}
                                </ul>
                            </div>&nbsp;
                        </Authorization>
                        <button className="btn btn-primary" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive">
                                <Table rowKeyField="id" data={data}>
                                    <Column field="visit" header={<MatchSortHeader title="Site ID" field="visit/subject/site/id" options={siteOptions} sortField="visit/subject/site/number" />} formatter={visit => visit.subject.site.number} />
                                    <Column field="visit" header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="visit/subject/id" options={subjectOptions} sortField="visit/subject/number" />} formatter={visit => visit.subject.number} />
                                    <Column field="visit" header={<MatchSortHeader title={fieldDisplayText.SubjectType} field="visit/subject/status" options={subjectTypesOptions} sortField="visit/subject/status" />} formatter={visit => subjectTypes.find(st => st.id === visit.subject.status).name} />
                                    {displayRandomisationColumn() ? <Column field="visit" automationId="randomization-status" header={<MatchSortHeader title="Randomization Status" field="visit/subject/isRandomized" options={booleanOptions} sortField="visit/subject/randomizationNumber" />} formatter={visit => visit.subject.randomizationNumber ? "Yes" : "No"} /> : null}
                                    <Column field="visit" header={<MatchSortHeader title="Visit" field="visit/name" options={visitOptions} sortField="visit/name" />} formatter={visit => visit.name} />
                                    <Column field="name" header={<MatchSortHeader title="Session" field="name" options={sessionOptions} sortField="name" />} formatter={name => name} />
                                    <Column field="" header={<MatchSortHeader title="Device Serial Number" field="serialNumber" options={serialNumberOptions} sortField="serialNumber" />} formatter={session => session.serialNumber ? deviceTypes.find(dt => dt.id === session.deviceType).serialNumberPrefix + session.serialNumber : "-"} />
                                    <Column field="recordingStart" header={<DateMatchSortHeader title="Recording Start" field="recordingStart" sortField="recordingStart" />} formatter={rs => formatDateTime(rs)} />
                                    <Column field="lastReceived" header={<DateMatchSortHeader title="Last Received" field="lastReceived" sortField="lastReceived" />} formatter={ls => formatDateTime(ls)} />
                                    <Column field="lastAnalysed" header={<SortHeader title="Analysis Started" field="lastAnalysed" />} formatter={la => formatDateTime(la)} />
                                    <Column field="status" header={<MatchSortHeader title="Status" field="status" options={coughSessionStatusesOptions} sortField="status" />} formatter={status => coughSessionStatuses.find(css => css.id === status).name} />
                                    <Column field="configured" header={<MatchSortHeader title="Is Configured" field="configured" options={booleanOptions} sortField="configured" />} formatter={configured => configured ? "Yes" : "No"} />
                                    <Column field="suppressed" header={<MatchSortHeader title="Suppressed" field="suppressed" options={booleanOptions} sortField="suppressed" />} formatter={suppressed => suppressed ? "Yes" : "No"} />
                                    <Column field="" formatter={RenderHamburgerMenu} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showTrackingModal ? <TrackingNumberModal
                record={selectedCoughSession}
                onClose={() => { setShowTrackingModal(false); applyFilters([]); }}
            /> : null}
            {showPostalModal ? <PostalReceivedModal
                record={selectedCoughSession}
                onClose={() => setShowPostalModal(false)}
            /> : null}
            <UnassignDeviceAuth
                record={selectedCoughSession}
            >
                {showUnassignModal ? <UnassignDeviceModal
                    record={selectedCoughSession}
                    onClose={() => setShowUnassignModal(false)}
                    onCompleted={() => {
                        setShowUnassignModal(false);
                        applyFilters([]);
                    }}
                /> : null}
            </UnassignDeviceAuth>
            {showPrimaryAnalysisSetModal ?
                <PrimaryAnalysisSetModal
                    session={selectedCoughSession}
                    onClose={() => setShowPrimaryAnalysisSetModal(false)}
                /> : null}
            {showReprocessModal ?
                <ReprocessCoughSessionModal
                    coughSession={selectedCoughSession}
                    onClose={() => setShowReprocessModal(false)}
                /> : null}
            {showBatchForSessionsModal ?
                <BatchForSessionsModal
                    onClose={() => setShowBatchForSessionsModal(false)}
                />
                : null}
            {showBatchReprocessModal !== 'hidden' ?
                <BatchReprocessModal
                    algorithm={showBatchReprocessModal}
                    onClose={() => setShowBatchReprocessModal('hidden')}
                />
                : null}
        </>
    );
}
